import { parseCatalog } from "../lib/catalog";
import type { Catalog, PlaybackAccess } from "../types";

// Modo de desarrollo local: VITE_DEV_CATALOG=/dev-media/catalog.json sirve un catálogo de prueba
// (generado por scripts/make_dev_catalog.py) sin Cloudflare ni Backblaze.
const DEV_CATALOG = import.meta.env.VITE_DEV_CATALOG as string | undefined;

export class ApiError extends Error {
  constructor(message: string, public status: number, public code = "error") {
    super(message);
  }
}

async function call<T>(path: string, init?: RequestInit): Promise<T> {
  let r: Response;
  try {
    r = await fetch(path, { credentials: "same-origin", ...init });
  } catch {
    throw new ApiError("No hay conexión con Radiant TV. Revisa tu Internet.", 0, "network");
  }
  if (!r.ok) {
    let msg = `Error ${r.status}`;
    let code = "error";
    try {
      const body = (await r.json()) as { message?: string; error?: string };
      msg = body.message ?? msg;
      code = body.error ?? code;
    } catch {
      /* respuesta sin JSON */
    }
    throw new ApiError(msg, r.status, code);
  }
  return (await r.json()) as T;
}

export interface Session {
  authenticated: boolean;
  public: boolean;
  configured: boolean;
  missing: string[];
  progressSync: boolean;
}

export const isDev = Boolean(DEV_CATALOG);

export async function getSession(): Promise<Session> {
  if (DEV_CATALOG) return { authenticated: true, public: true, configured: true, missing: [], progressSync: false };
  return call<Session>("/api/session");
}

export async function login(password: string): Promise<void> {
  await call("/api/login", { method: "POST", headers: { "Content-Type": "application/json" }, body: JSON.stringify({ password }) });
}

export async function logout(): Promise<void> {
  await call("/api/logout", { method: "POST" });
}

let etag: string | null = null;
let lastCatalog: Catalog | null = null;

/** Descarga el catálogo; con ETag, si no cambió devuelve el anterior sin volver a procesarlo. */
export async function getCatalog(): Promise<Catalog> {
  const url = DEV_CATALOG ?? "/api/catalog";
  const headers: Record<string, string> = {};
  if (etag && lastCatalog) headers["If-None-Match"] = etag;
  let r: Response;
  try {
    r = await fetch(url, { headers, credentials: "same-origin", cache: "no-cache" });
  } catch {
    if (lastCatalog) return lastCatalog;
    throw new ApiError("No hay conexión con Radiant TV. Revisa tu Internet.", 0, "network");
  }
  if (r.status === 304 && lastCatalog) return lastCatalog;
  if (!r.ok) {
    let msg = `No se pudo cargar el catálogo (${r.status}).`;
    let code = "error";
    try {
      const b = (await r.json()) as { message?: string; error?: string };
      msg = b.message ?? msg;
      code = b.error ?? code;
    } catch {
      /* sin cuerpo */
    }
    throw new ApiError(msg, r.status, code);
  }
  lastCatalog = parseCatalog(await r.json());
  etag = r.headers.get("ETag");
  return lastCatalog;
}

let access: PlaybackAccess | null = null;
let pending: Promise<PlaybackAccess> | null = null;

/** Acceso temporal a los archivos de B2. Se renueva 30 minutos antes de caducar. */
export async function getPlaybackAccess(force = false): Promise<PlaybackAccess> {
  if (DEV_CATALOG) {
    return { base: new URL(DEV_CATALOG.replace(/[^/]+$/, ""), location.origin).href, token: null, expiresAt: Infinity };
  }
  if (!force && access && access.expiresAt - Date.now() > 30 * 60_000) return access;
  pending ??= call<PlaybackAccess>("/api/playback").then((a) => {
    access = a;
    pending = null;
    return a;
  }, (e) => {
    pending = null;
    throw e;
  });
  return pending;
}

export function currentAccess(): PlaybackAccess | null {
  return access ?? (DEV_CATALOG ? { base: new URL(DEV_CATALOG.replace(/[^/]+$/, ""), location.origin).href, token: null, expiresAt: Infinity } : null);
}
